import React, { ReactNode } from 'react';

import * as FlipdishApi from '@flipdish/api-client-typescript';
import {
  Button,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Theme,
  withStyles,
} from '@material-ui/core';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import classNames from 'classnames';
import { withSnackbar, withSnackbarProps } from 'notistack';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { compose } from 'recompose';

import { accountActions } from '../../actions/account.actions';
import { accountConstants } from '../../constants/account.constants';
import { passwordValidationApiErrors, validatePassword } from '../../helpers/validation';
import { createLoadingSelector } from '../../selectors/loading.selector';
import { LoadingButton } from '../../ui/LoadingButton';
import { PinCodeInput } from '../Auth/Login/PinCodeInput';

interface IMaterialUiProps {
  classes?: any;
}

interface IDispatchProps {
  dispatch: (input: any) => any;
}

interface IStoreStateProps {
  account: any;
  translate: (input: string) => string;
  loading: boolean;
}

interface IProfilePasswordProps {
  open?: boolean;
  currentPassword?: string;
  currentPasswordErrorMessage?: string;
  newPassword?: string;
  pin?: string;
  pinErrorMessage?: string;
  newPasswordErrorMessage?: string;
  currentPasswordMasked?: boolean;
  newPasswordMasked?: boolean;
  updatedPasswordSuccess?: boolean;
  onChange?: () => void;
  successMessage?: string;
  children?: (onClick: Function) => ReactNode;
}

type InnerProps = IMaterialUiProps & IDispatchProps & IStoreStateProps & withSnackbarProps;

type OuterProps = IProfilePasswordProps;

export type Props = InnerProps & OuterProps;

const downBreakpoint = 'sm';

const styles = (theme: Theme) =>
  createStyles({
    passwordButton: {
      margin: '0px 0px 12px 0px',
      padding: '4px 0px 4px 0px',
      minHeight: '24px',
      maxHeight: '24px',
      lineHeight: '1.5',
      letterSpacing: '0.2px',
      textTransform: 'none',
      [theme.breakpoints.down(downBreakpoint)]: {
        marginLeft: '16px',
        padding: '4px 0px 4px 0px',
      },
    },
    dialog: {
      minWidth: '340px',
      maxWidth: '340px',
    },
    textFieldContainer: {
      display: 'flex',
      alignItems: 'flex-end',
    },
    currentPassword: {
      marginTop: '8px',
      marginBottom: '36px',
    },
    newPassword: {
      marginBottom: '24px',
    },
    textField: {
      minWidth: '200px',
      margin: 0,
    },
    visibilityIcons: {
      marginLeft: '8px',
      opacity: 0.54,
    },
    newPasswordVisibilityIcon: {
      marginBottom: '20px',
    },
  });

class ProfilePasswordOLD extends React.Component<Props, any> {
  public constructor(props) {
    super(props);
    this.state = {
      open: false,
      currentPassword: '',
      currentPasswordMasked: true,
      newPassword: '',
      newPasswordMasked: true,
      pin: '',
    };
  }

  public componentDidUpdate(prevProps) {
    const {
      updatedPasswordSuccess,
      onChange,
      enqueueSnackbar,
      successMessage,
      translate,
    } = this.props;

    if (prevProps.updatedPasswordSuccess !== updatedPasswordSuccess && updatedPasswordSuccess) {
      if (onChange) {
        onChange();
      }

      if (successMessage) {
        enqueueSnackbar(translate(successMessage), {
          variant: 'success',
        });
      }

      this.setState({
        open: false,
        currentPassword: '',
        newPassword: '',
      });
    }
  }

  public render() {
    const { account, translate, classes, children } = this.props;
    const { pinErrorMessage } = this.state;
    let currentPasswordError;
    if (
      account.error?.length > 0 &&
      account.IsVerified &&
      !passwordValidationApiErrors.includes(account.error)
    ) {
      currentPasswordError = account.error.replace('.', '').replace(' ', '_');
    } else {
      currentPasswordError = undefined;
    }

    let pinError = false;
    let pinHelperText: string | undefined;
    if (currentPasswordError === 'Pin_incorrect') {
      pinError = true;
      pinHelperText = translate('Invalid_pin');
      currentPasswordError = undefined;
    } else {
      pinError = !!pinErrorMessage;
    }

    // Original content
    let dialogContent = [
      <DialogTitle key="cptitle">{translate('Change_password')}</DialogTitle>,
      <DialogContent key="cpcontent">
        <div className={classNames([classes.currentPassword, classes.textFieldContainer])}>
          <TextField
            className={classes.textField}
            type={this.state.currentPasswordMasked ? 'password' : 'text'}
            error={currentPasswordError != undefined || this.state.currentPasswordErrorMessage}
            label={translate('Your_current_password')}
            value={this.state.currentPassword}
            helperText={
              currentPasswordError != undefined
                ? translate(currentPasswordError)
                : this.state.currentPasswordErrorMessage &&
                  translate(this.state.currentPasswordErrorMessage)
            }
            onChange={this.onChange('currentPassword')}
            inputProps={{
              'data-fd': 'txt-password-change-old-password',
            }}
          />
          {this.state.currentPasswordMasked ? (
            <Visibility
              className={
                currentPasswordError != undefined
                  ? classNames(classes.visibilityIcons, classes.newPasswordVisibilityIcon)
                  : classes.visibilityIcons
              }
              onClick={this.visibilityOn('currentPassword')}
              data-fd="txt-password-change-old-password-visibility-on"
            />
          ) : (
            <VisibilityOff
              className={
                currentPasswordError != undefined
                  ? classNames(classes.visibilityIcons, classes.newPasswordVisibilityIcon)
                  : classes.visibilityIcons
              }
              onClick={this.visibilityOff('currentPassword')}
              data-fd="txt-password-change-old-password-visibility-off"
            />
          )}
        </div>
        <div className={classNames(classes.textFieldContainer, classes.newPassword)}>
          <TextField
            placeholder={translate('At_least_12_characters')}
            className={classes.textField}
            type={this.state.newPasswordMasked ? 'password' : 'text'}
            error={this.state.newPasswordErrorMessage != undefined}
            label={translate('Set_a_new_password')}
            value={this.state.newPassword}
            helperText={
              this.state.newPasswordErrorMessage || translate('Password_must_contain_two_of')
            }
            onChange={this.onChange('newPassword')}
            inputProps={{
              'data-fd': 'txt-password-change-new-password',
            }}
          />
          {this.state.newPasswordMasked ? (
            <Visibility
              className={classNames(classes.visibilityIcons, classes.newPasswordVisibilityIcon)}
              onClick={this.visibilityOn('newPassword')}
              data-fd="txt-password-change-new-password-visibility-on"
            />
          ) : (
            <VisibilityOff
              className={classNames(classes.visibilityIcons, classes.newPasswordVisibilityIcon)}
              onClick={this.visibilityOff('newPassword')}
              data-fd="txt-password-change-new-password-visibility-off"
            />
          )}
        </div>
      </DialogContent>,
      <DialogActions key="cpactions">
        <Button onClick={this.handleClose} color="primary" data-fd="btn-password-change-cancel">
          {translate('Cancel')}
        </Button>

        <div className={classes.buttonOuterWrapper}>
          <LoadingButton
            buttonProps={{
              color: 'primary',
              onClick: this.handleChange,
              autoFocus: true,
              'data-fd': 'btn-password-change-agree',
            }}
            wrapperClassName={classes.buttonInnerWrapper}
            loadingResources={[accountConstants.UPDATE_PASSWORD]}
          >
            {translate('Change')}
          </LoadingButton>
        </div>
      </DialogActions>,
    ];

    if (!account.IsVerified) {
      // Pin content
      dialogContent = [
        <DialogTitle key="cptitle1">{translate('Set_a_new_password')}</DialogTitle>,
        <DialogContent key="cpcontent1">
          <DialogContentText>{translate('Please_check_email_for_pin')}</DialogContentText>
          <div className={classNames(classes.textFieldContainer, classes.newPassword)}>
            <TextField
              placeholder={translate('At_least_12_characters')}
              className={classes.textField}
              type={this.state.newPasswordMasked ? 'password' : 'text'}
              error={this.state.newPasswordErrorMessage != undefined}
              label={translate('Set_a_new_password')}
              value={this.state.newPassword}
              helperText={
                this.state.newPasswordErrorMessage || translate('Password_must_contain_two_of')
              }
              onChange={this.onChange('newPassword')}
              inputProps={{
                'data-fd': 'txt-password-change-new-password',
              }}
            />
            {this.state.newPasswordMasked ? (
              <Visibility
                className={classNames(classes.visibilityIcons, classes.newPasswordVisibilityIcon)}
                onClick={this.visibilityOn('newPassword')}
                data-fd="txt-password-change-new-password-visibility-on"
              />
            ) : (
              <VisibilityOff
                className={classNames(classes.visibilityIcons, classes.newPasswordVisibilityIcon)}
                onClick={this.visibilityOff('newPassword')}
                data-fd="txt-password-change-new-password-visibility-off"
              />
            )}
          </div>
          <PinCodeInput onChange={this.setPinValue} error={pinError} helperText={pinHelperText} />
        </DialogContent>,
        <DialogActions key="cpactions1">
          <Button onClick={this.handleClose} color="primary" data-fd="btn-password-change-cancel">
            {translate('Cancel')}
          </Button>

          <div className={classes.buttonOuterWrapper}>
            <LoadingButton
              buttonProps={{
                color: 'primary',
                onClick: this.handleChange,
                autoFocus: true,
                'data-fd': 'btn-password-change-agree',
              }}
              wrapperClassName={classes.buttonInnerWrapper}
              loadingResources={[
                accountConstants.UPDATE_PASSWORD,
                accountConstants.CREATE_PASSWORD_WITH_PIN,
              ]}
            >
              {translate('Confirm_password')}
            </LoadingButton>
          </div>
        </DialogActions>,
      ];
    }

    return (
      <div>
        {children ? (
          children(this.changePassword)
        ) : (
          <Button
            color="primary"
            className={classes.passwordButton}
            onClick={this.changePassword}
            data-fd="btn-password-change-open"
          >
            {!account.IsVerified ? translate('Set_a_new_password') : translate('Change_password')}
          </Button>
        )}
        <Dialog
          open={this.state.open}
          onClose={this.handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            className: classes.dialog,
          }}
        >
          {dialogContent}
        </Dialog>
      </div>
    );
  }

  private onChange = (entityName: string) => (event: any) => {
    this.setState({
      [entityName]: event.target.value,
      [entityName + 'ErrorMessage']: undefined,
    });
  };

  private setPinValue = (value) => {
    this.setState({
      pin: value,
      pinErrorMessage: undefined,
    });
  };

  private visibilityOn = (entityName: string) => (event: any) => {
    this.setState({ [entityName + 'Masked']: false });
  };

  private visibilityOff = (entityName: string) => (event: any) => {
    this.setState({ [entityName + 'Masked']: true });
  };

  private changePassword = (event: React.MouseEvent<HTMLElement>) => {
    const { account, dispatch } = this.props;
    if (!account.IsVerified) {
      dispatch(accountActions.requestPasswordChangePin(account.Email));
    }
    this.setState({ open: true });
  };

  private handleClose = () => {
    this.setState({
      open: false,
      currentPassword: '',
      newPassword: '',
      currentPasswordErrorMessage: undefined,
      newPasswordErrorMessage: undefined,
      currentPasswordMasked: true,
      newPasswordMasked: true,
      pin: '',
      pinErrorMessage: undefined,
    });
  };

  private handleChange = (event: React.MouseEvent<HTMLElement>) => {
    const { account, dispatch, translate } = this.props;
    const { currentPassword, newPassword, pin } = this.state;
    let currentPasswordErrorMessage: string | undefined;
    let pinErrorMessage: string | undefined;
    let changePasswordModel;
    let action;
    if (!account.IsVerified) {
      changePasswordModel = { Pin: 0, NewPassword: newPassword };
      if (!pin || pin.length < 6) {
        pinErrorMessage = 'pin_must_be_6_digits'; // can't be empty
      } else {
        const pinInt = parseInt(pin, 10);

        if ((pinInt + '').length < 6) {
          pinErrorMessage = 'pin_must_be_6_digits'; // can't be empty
        }

        changePasswordModel.Pin = pinInt;
        action = accountActions.createPasswordWithPin(changePasswordModel);
      }
    } else {
      changePasswordModel = new FlipdishApi.ChangePasswordModel();
      changePasswordModel.NewPassword = newPassword ? newPassword : '';
      changePasswordModel.OldPassword = currentPassword ? currentPassword : '';
      action = accountActions.updatePassword(changePasswordModel);

      if (!changePasswordModel.OldPassword) {
        currentPasswordErrorMessage = 'Required'; // can't be empty
      } else if (
        changePasswordModel.OldPassword.length >
        changePasswordModel.OldPassword.trimLeft().trimRight().length
      ) {
        currentPasswordErrorMessage = 'No_leading_or_trailing_space'; // can't start \ end with space
      }
    }

    const newPasswordErrorMessage = validatePassword(changePasswordModel.NewPassword, translate);

    if (currentPasswordErrorMessage || newPasswordErrorMessage || pinErrorMessage) {
      this.setState({
        currentPasswordErrorMessage,
        newPasswordErrorMessage,
        pinErrorMessage,
      });
      return;
    }

    dispatch(action);
  };
}

function mapStateToProps(state) {
  const { account, profile, locale } = state;
  return {
    account: account,
    profile: profile,
    translate: getTranslate(locale),
    loading:
      createLoadingSelector([accountConstants.UPDATE_PASSWORD])(state) ||
      createLoadingSelector([accountConstants.CREATE_PASSWORD_WITH_PIN])(state),
    updatedPasswordSuccess: state.account.updatePasswordSuccess,
  };
}

const EnhancedComponent = compose<InnerProps, OuterProps>(
  withStyles(styles, {
    name: 'ProfilePasswordOLD',
  }),
  connect(mapStateToProps),
  withSnackbar
)(ProfilePasswordOLD);

export { EnhancedComponent as ProfilePasswordOLD };
